import { CompactSwitchControlledCard } from 'lib/components/Card/CompactSwitchControlledCard';
import { GridInput } from 'lib/components/Card/Grid';
import RadioButtonGroup from 'lib/components/Checkbox/RadioButtonGroup';
import CurrencyTextField from 'lib/components/CurrencyTextField';
import { isDefined } from 'lib/helpers/typeGuards';
import { AdRate, ERate } from 'lib/types';
import { OrderRate } from 'lib/types/rates';
import { getShouldForceGrayscale } from 'routes/ads/place/steps/colorHelpers';

type ColorFeesProps = {
  onChange: (newRate: AdRate | ERate | OrderRate) => void;
  value: OrderRate;
};

function ColorFees({ value, onChange }: ColorFeesProps) {
  const shouldRequireGrayscale = getShouldForceGrayscale(value);
  return (
    <CompactSwitchControlledCard
      labelProps={{
        label: 'Enable color options?',
        description: 'Show selected color options in the placement flow.',
        value: !!value.colorFees,
        onChange: newValue => {
          onChange({
            ...value,
            colorFees: newValue ? { flatFee: 0 } : null
          });
        }
      }}
      background="white"
    >
      <RadioButtonGroup
        id="grayscale-options"
        labelText="Select color options"
        options={[
          {
            labelText: 'Allow either color or grayscale',
            value: 'allowGrayscale'
          },
          {
            labelText: 'Require grayscale',
            value: 'requireGrayscale'
          }
        ]}
        value={shouldRequireGrayscale ? 'requireGrayscale' : 'allowGrayscale'}
        onChange={newValue => {
          const { ...updatedFees } = value.colorFees ?? {};
          onChange({
            ...value,
            colorFees:
              newValue === 'requireGrayscale'
                ? {}
                : { flatFee: 0, ...updatedFees }
          });
        }}
      />
      <GridInput fullWidth>
        <CurrencyTextField
          id="color-flat-fee-input"
          labelText="Flat fee for all color placements"
          initialValue={value.colorFees?.flatFee}
          onChange={newValue => {
            const { flatFee, ...updatedFees } = value.colorFees ?? {};

            onChange({
              ...value,
              colorFees: { ...updatedFees, flatFee: newValue || 0 }
            });
          }}
          disabled={shouldRequireGrayscale}
        />
      </GridInput>
      <GridInput fullWidth>
        <CompactSwitchControlledCard
          labelProps={{
            label: 'Enable text color?',
            description:
              'Allow the user to choose from a set of text colors in the placement flow.',
            value: isDefined(value.colorFees?.textColorFee),
            onChange: newValue => {
              const { textColorFee, ...updatedFees } = value.colorFees ?? {};

              onChange({
                ...value,
                colorFees: {
                  ...updatedFees,
                  ...(newValue ? { textColorFee: 0 } : {})
                }
              });
            }
          }}
          disabled={shouldRequireGrayscale}
        >
          <CurrencyTextField
            initialValue={value.colorFees?.textColorFee ?? 0}
            onChange={newValue => {
              const { textColorFee, ...updatedFees } = value.colorFees ?? {};

              onChange({
                ...value,
                colorFees: {
                  ...updatedFees,
                  textColorFee: newValue ?? 0
                }
              });
            }}
            id="text-color-fee-input"
            labelText="Fee"
          />
        </CompactSwitchControlledCard>
      </GridInput>
      <GridInput fullWidth>
        <CompactSwitchControlledCard
          labelProps={{
            label: 'Enable border color?',
            description:
              'Allow the user to choose from a set of border colors in the placement flow.',
            value: isDefined(value.colorFees?.borderColorFee),
            onChange: newValue => {
              const { borderColorFee, ...updatedFees } = value.colorFees ?? {};

              onChange({
                ...value,
                colorFees: {
                  ...updatedFees,
                  ...(newValue ? { borderColorFee: 0 } : {})
                }
              });
            }
          }}
          disabled={shouldRequireGrayscale}
        >
          <CurrencyTextField
            initialValue={value.colorFees?.borderColorFee ?? 0}
            onChange={newValue => {
              const { borderColorFee, ...updatedFees } = value.colorFees ?? {};

              onChange({
                ...value,
                colorFees: {
                  ...updatedFees,
                  borderColorFee: newValue ?? 0
                }
              });
            }}
            id="border-color-fee-input"
            labelText="Fee"
          />
        </CompactSwitchControlledCard>
      </GridInput>
      <GridInput fullWidth>
        <CompactSwitchControlledCard
          labelProps={{
            label: 'Enable background color?',
            description:
              'Allow the user to choose from a set of background colors in the placement flow.',
            value: isDefined(value.colorFees?.backgroundColorFee),
            onChange: newValue => {
              const { backgroundColorFee, ...updatedFees } =
                value.colorFees ?? {};

              onChange({
                ...value,
                colorFees: {
                  ...updatedFees,
                  ...(newValue ? { backgroundColorFee: 0 } : {})
                }
              });
            }
          }}
          disabled={shouldRequireGrayscale}
        >
          <CurrencyTextField
            initialValue={value.colorFees?.backgroundColorFee ?? 0}
            onChange={newValue => {
              const { backgroundColorFee, ...updatedFees } =
                value.colorFees ?? {};

              onChange({
                ...value,
                colorFees: {
                  ...updatedFees,
                  backgroundColorFee: newValue ?? 0
                }
              });
            }}
            id="background-color-fee-input"
            labelText="Fee"
          />
        </CompactSwitchControlledCard>
      </GridInput>
    </CompactSwitchControlledCard>
  );
}

export default ColorFees;
